import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api/client";

export type SettingStepStatus = "not_started" | "started" | "ok";

export type UseYardSettingStepsResult = {
  /** Status per step key. A missing key means "not started". */
  statuses: Record<string, SettingStepStatus>;
  loading: boolean;
  setStatus: (step: string, status: SettingStepStatus) => Promise<void>;
};

/**
 * Manages the manual progress status of a yard's configuration steps
 * (screen 903). Statuses are persisted per yard and step; the absence of a
 * stored value means "not started".
 */
export function useYardSettingSteps(yardId: number): UseYardSettingStepsResult {
  const [statuses, setStatuses] = useState<Record<string, SettingStepStatus>>(
    {},
  );
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET(
      "/api/admin/yards/setting-steps",
      { params: { query: { yardId } } },
    );
    if (error || !data) {
      setLoading(false);
      return;
    }

    const next: Record<string, SettingStepStatus> = {};
    for (const item of data.data.steps) {
      next[item.step] = item.status;
    }
    setStatuses(next);
    setLoading(false);
  }, [yardId]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const setStatus = useCallback(
    async (step: string, status: SettingStepStatus) => {
      setStatuses((prev) => {
        const next = { ...prev };
        if (status === "not_started") {
          delete next[step];
        } else {
          next[step] = status;
        }
        return next;
      });

      await apiClient.PUT("/api/admin/yards/setting-steps/{step}", {
        params: { path: { step } },
        body: { yardId, status },
      });
    },
    [yardId],
  );

  return { statuses, loading, setStatus };
}
